// src/components/Production/ProductionSettings.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useStudio } from "../../contexts/StudioContext";
import { useFeedback } from "../../contexts/FeedbackContext";
import { useNotifications } from "../../contexts/NotificationsContext";

const LEGACY_KEY = "mudoProductionSettings";

// Safe number parsers
const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};
const toFloat = (v, def = 1.0) => {
  const n = Number.parseFloat(String(v ?? "").trim());
  return Number.isFinite(n) ? n : def;
};

export default function ProductionSettings() {
  const { expectedOutput, complexityFactor, updateStudio } = useStudio();
  const { toast } = useFeedback?.() || { toast: null };
  const { notifyAndInbox } = useNotifications() || {};

  const [output, setOutput] = useState("");
  const [complexity, setComplexity] = useState("1.0");
  const [dirty, setDirty] = useState(false);

  // Seed UI from context (with fallback to legacy LS if context empty)
  useEffect(() => {
    let eo = expectedOutput;
    let cf = complexityFactor;

    if ((eo == null || cf == null)) {
      try {
        const legacy = JSON.parse(localStorage.getItem(LEGACY_KEY) || "null");
        if (legacy && typeof legacy === "object") {
          if (eo == null && legacy.productionOutput != null) eo = legacy.productionOutput;
          if (cf == null && legacy.studioComplexityFactor != null) cf = legacy.studioComplexityFactor;
        }
      } catch {}
    }

    const eoN = Number(eo ?? 0);
    const cfN = Number(cf ?? 1.0);

    setOutput(Number.isFinite(eoN) ? String(eoN) : "");
    setComplexity(Number.isFinite(cfN) ? String(cfN) : "1.0");
    setDirty(false);
  }, [expectedOutput, complexityFactor]);

  const previewCopy = useMemo(() => {
    const eo = Math.max(0, toInt(output, 0));
    const cf = Math.max(0.5, Math.min(2.0, toFloat(complexity, 1.0)));
    // Super-light copy to hint at downstream effects
    return `Planner will estimate durations using a ${cf.toFixed(2)}× modifier.
Target throughput ≈ ${eo}/month.`;
  }, [output, complexity]);

  const handleSave = () => {
    const eo = Math.max(0, toInt(output, 0));
    const cf = Math.max(0.5, Math.min(2.0, toFloat(complexity, 1.0)));

    updateStudio?.({ expectedOutput: eo, complexityFactor: cf });

    // Persist legacy mirror
    try {
      localStorage.setItem(
        LEGACY_KEY,
        JSON.stringify({
          productionOutput: eo,
          studioComplexityFactor: cf,
        })
      );
    } catch {}

  setDirty(false);
  notifyAndInbox?.({
    notification: {
      id: "production-settings-saved",
      title: "Production settings",
      message: "Production settings saved",
      level: "success",
      source: "system",
      world: "studio",
      action: { kind: "NAVIGATE", to: "/dashboard/settings" },
    },
  });
  if (toast) toast("Production settings saved");
  else if (!notifyAndInbox) alert("Production settings saved!");
  };

  const resetDefaults = () => {
    setOutput("0");
    setComplexity("1.0");
    setDirty(true);
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
  <h1 className="text-2xl font-bold mb-2">Production Settings</h1>
      <p className="text-sm opacity-70 mb-6">
        Define your studio’s baseline capacity and workflow complexity. These values inform
        the Planner’s stage timings, capacity balancing, and lead-time estimates.
      </p>

      {/* Expected Output */}
      <div>
        <label className="block text-zinc-700 dark:text-white font-medium">
          Expected Monthly Output
        </label>
        <p className="text-xs opacity-70 mb-2">
          Average number of pieces completed per month. Used for capacity planning and forecasting.
        </p>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          step={1}
          value={output}
          onChange={(e) => { setOutput(e.target.value); setDirty(true); }}
          className="w-full px-3 py-2 rounded border border-white/10 bg-zinc-900 text-white mb-4"
          placeholder="e.g. 250"
          aria-label="Expected monthly output"
        />
      </div>

      {/* Studio Complexity */}
      <div>
        <label className="block text-zinc-700 dark:text-white font-medium">
          Studio Complexity
        </label>
        <p className="text-xs opacity-70 mb-2">
          Adjusts production times for your setup. Higher factors increase estimated durations.
        </p>
        <select
          value={complexity}
          onChange={(e) => { setComplexity(e.target.value); setDirty(true); }}
          className="w-full px-3 py-2 rounded border border-white/10 bg-zinc-900 text-white mb-2"
          aria-label="Studio complexity factor"
        >
          <option value="0.8">Basic (0.8×) — minimal equipment, solo work</option>
          <option value="1.0">Standard (1.0×) — normal operation</option>
          <option value="1.15">Shared Facility (1.15×) — shared kilns or delays</option>
          <option value="1.25">Advanced Setup (1.25×) — complex workflow or larger team</option>
        </select>

        {/* Live preview helper */}
        <div className="text-xs opacity-70">{previewCopy}</div>
      </div>

      {/* Info Banner */}
      <div className="text-sm bg-blue-500/10 border border-blue-500/30 text-blue-300 rounded p-3">
        <b>Tip:</b> These settings feed into auto-generated batches, drying and firing timelines,
        and daily planner estimates.
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={resetDefaults}
          className="px-3 py-2 rounded border border-white/10 bg-zinc-800 text-white hover:bg-zinc-700"
          title="Reset to sensible defaults"
        >
          Reset
        </button>

        <button
          onClick={handleSave}
          disabled={!dirty}
          className={`px-4 py-2 rounded text-white ${dirty ? "bg-orange-600 hover:bg-orange-700" : "bg-zinc-600 cursor-not-allowed"}`}
        >
          Save Settings
        </button>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
        <div className="font-semibold mb-1">Legal</div>
        <p className="opacity-80">
          View our{" "}
          <a className="underline" href="/dashboard/legal/terms" target="_blank" rel="noreferrer">
            Terms &amp; Conditions
          </a>.
        </p>
      </div>
    </div>
  );
}
